/**
 * The icons a folder can wear, drawn from the app's own drawables.
 *
 * `tools/sync-icons.mjs` converts every icon of the app's `iconsTag` map (and
 * any drawable named in `src/icons/extra-icons.json`) into `src/icons/generated.ts`,
 * keyed the way `iconKeyOf` keys a tag's icon. A new icon in the app arrives by
 * rerunning `npm run icons` and committing the regenerated module; a drawable
 * the web wants for a component of its own goes into `extra-icons.json` first.
 *
 * A name the generated set does not hold draws nothing, which is the same
 * outcome as carrying no icon. Every icon is filled in `currentColor`.
 */
import type { ComponentType } from 'react';
import { ICONS } from '../icons/generated';
import type { VectorIcon } from '../icons/generated';

interface IconProps {
  size: number;
}

const box = (size: number) => ({ width: size, height: size, display: 'block' as const });

function drawn(icon: VectorIcon): ComponentType<IconProps> {
  return function Icon({ size }: IconProps) {
    return (
      <svg viewBox={icon.viewBox} aria-hidden="true" style={box(size)} fill="currentColor">
        {icon.paths.map((path, i) => (
          <path key={i} d={path.d} fillRule={path.evenOdd ? 'evenodd' : undefined} />
        ))}
      </svg>
    );
  };
}

/** One component per key, so a re-render never hands React a new type. */
const components = new Map<string, ComponentType<IconProps>>();

export function iconFor(key: string | null): ComponentType<IconProps> | null {
  if (!key || !Object.hasOwn(ICONS, key)) return null;
  let component = components.get(key);
  if (!component) {
    component = drawn(ICONS[key]!);
    components.set(key, component);
  }
  return component;
}

/** The bare tag shape. A plain tag is this alone; a notebook wears it
 *  inside its tile when it has no icon of its own. */
export const FolderGlyph: ComponentType<IconProps> = drawn(ICONS['local_offer']!);
